import { useAnalytics } from '@/components/molecules/AnalyticsProvider';
import { MakeTile } from '@/components/molecules/MakeTile';
import { BuildAndPriceTemplate } from '@/components/template/BuildAndPriceTemplate/BuildAndPriceTemplate';
import { useZipContext } from '@/context/ZipContext';
import { useSearchQuery } from '@/hooks/useQuery';
import {
  DEFAULT_US_ZIP_CODE,
  GOOGLE_TRACK_INFO,
  HIDDEN_MAKES,
  POPULAR_MAKES,
} from '@/lib/constants';
import { getSeoTagParser } from '@/lib/seo/seoTags';
import { BasePageProps } from '@/types/page';
import { MakePageState } from '@/types/pageStates';
import { GetStaticPaths, GetStaticProps } from 'next';
import dynamic from 'next/dynamic';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useRouter } from 'next/router';
import { FC, useState } from 'react';

// Dynamically import LocationPopOutModal to avoid SSR issues with Leaflet
const LocationPopOutModal = dynamic(
  () =>
    import(
      '@/components/organisms/LocationPopOutModal/LocationPopOutModal'
    ).then((mod) => mod.LocationPopOutModal),
  { ssr: false }
);

interface BuildByLocationPageProps {
  makes: string[];
}

const MakeTileItem: FC<{ make: string }> = ({ make }) => {
  const router = useRouter();
  const { handleActionTracking } = useAnalytics();
  const {
    data: { location, make: selectedMakeFromPath },
  } = useSearchQuery<MakePageState>('/build/[location]/[make]');
  const selected = selectedMakeFromPath === make;
  const { preferredZip } = useZipContext();

  let postal: string;
  if (location) {
    postal = location;
  } else if (preferredZip) {
    postal = preferredZip;
  } else {
    postal = DEFAULT_US_ZIP_CODE;
  }

  async function handleMakeClick() {
    const makeLink = `/build/${postal}/${make}`;
    handleActionTracking({
      ...GOOGLE_TRACK_INFO.chooseMakeButton,
      pageTarget: makeLink,
    });
    await router.push(makeLink);
  }

  return (
    <MakeTile
      handleMakeClick={handleMakeClick}
      make={make}
      selected={selected}
    />
  );
};

const Page: FC<BuildByLocationPageProps> = ({ makes }) => {
  const [openModal, setOpenModal] = useState(false);

  const handleClose = () => {
    setOpenModal(false);
  };

  return (
    <BuildAndPriceTemplate title={'Choose make'}>
      <div className="flex flex-col gap-[20px]">
        <span className="text-title24px">Popular makes in US</span>
        <div className="grid w-full grid-cols-2 gap-[16px]">
          {POPULAR_MAKES.map((make) => (
            <MakeTileItem make={make} key={make} />
          ))}
        </div>
      </div>
      <div className="flex flex-col gap-[20px]">
        <div className="flex flex-col gap-[20px]">
          <div className="text-title20px">All makes</div>
          <div className="flex flex-col gap-[10px]">
            {makes.map((make) => (
              <MakeTileItem make={make} key={make} />
            ))}
          </div>
        </div>
      </div>

      <LocationPopOutModal open={openModal} close={handleClose} />
    </BuildAndPriceTemplate>
  );
};

export default Page;

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  const res = await fetch(`${process.env.API_ENDPOINT}/api/build/makes`);
  let makes: string[] = [];
  if (res.ok) {
    const data = await res.json();
    makes = data.Makes ?? [];
  }
  // Both US and CA share the same backend
  // However CA database does not have any data for build and price for now
  makes = makes.filter((make: string) => !HIDDEN_MAKES.includes(make));

  const { seoMetaObjTitle, seoMetaObjDescription, seoMetaKeywords } =
    getSeoTagParser({
      tagType: 'buildPage',
    });

  return {
    props: {
      ...(await serverSideTranslations(locale ?? 'en', ['common'])),
      makes,
      metadata: {
        title: seoMetaObjTitle,
        description: seoMetaObjDescription,
        keywords: seoMetaKeywords,
      },
    } satisfies BasePageProps<BuildByLocationPageProps>,
    revalidate: 60 * 10,
  };
};

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking',
  };
};
